import { useId, useRef, useState, type FormEvent } from 'react';
import { REJECTED_IS_FINAL_NOTE, REJECTION_EMAIL_NOTE } from '../reviewCopy';
import { ReviewDialog } from './ReviewDialog';

interface Props {
  periodLabel: string;
  busy: boolean;
  error: string | null;
  /** Called with the trimmed reason; never called for a blank one. */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/** Reject needs a reason. Blank / whitespace is stopped here, with an announced inline error — the server
 * independently enforces the same rule and its refusal is surfaced too. Opens with focus on the reason field. */
export function RejectRunDialog({ periodLabel, busy, error, onConfirm, onCancel }: Props) {
  const reasonId = useId();
  const reasonErrorId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setReasonError('Enter a reason for rejecting this payroll run.');
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    onConfirm(trimmed);
  }

  return (
    <ReviewDialog
      title="Reject this payroll run?"
      busy={busy}
      onClose={onCancel}
      initialFocusRef={reasonRef}
      description={
        <>
          <p>
            You are rejecting the payroll run for <strong>{periodLabel}</strong>. The reason is recorded with the
            decision.
          </p>
          <p className="muted">{REJECTED_IS_FINAL_NOTE}</p>
          <p className="muted">{REJECTION_EMAIL_NOTE}</p>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor={reasonId} className="payroll-review__label">
          Reason for rejection <span aria-hidden="true">*</span>
          <span className="payroll-review__sr-only"> (required)</span>
        </label>
        <textarea
          id={reasonId}
          ref={reasonRef}
          rows={4}
          value={reason}
          required
          aria-required="true"
          aria-invalid={reasonError ? true : undefined}
          aria-describedby={reasonError ? reasonErrorId : undefined}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError) setReasonError(null);
          }}
        />
        {reasonError && (
          <p id={reasonErrorId} className="error" role="alert">
            {reasonError}
          </p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="payroll-review__dialog-actions">
          <button type="button" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="payroll-review__danger" disabled={busy}>
            {busy ? 'Rejecting…' : 'Reject payroll run'}
          </button>
        </div>
      </form>
    </ReviewDialog>
  );
}
